<template>
  <v-dialog @input="close" :value="isOpened" max-width="720px">
    <v-card>
      <v-toolbar dark color="primary">
        <v-toolbar-title class="font-weight-bold">
          {{ dialogTitle }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn @click="close" icon>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pt-4 px-3">
        <v-form ref="form">
          <v-container>
            <v-row dense>
              <v-col cols="10">
                <div class="variant-field d-flex align-center">
                  <template v-if="selectedVariantImage">
                    <img
                      :src="selectedVariantImage"
                      alt="Variant Image"
                      class="variant-field__thumbnail mr-2" />
                  </template>
                  <template v-else>
                    <div
                      class="variant-field__thumbnail variant-field__thumbnail--placeholder mr-2" />
                  </template>
                  <v-autocomplete
                    class="flex-grow-1"
                    v-model="formData.arena_variant_id"
                    :items="variantOptions"
                    :disabled="isEditing"
                    :label="`${$t('entities.products.variant')}*`"
                    :rules="[requiredStringRule]"
                    item-text="text"
                    item-value="value"
                    hide-details />
                </div>
              </v-col>
              <v-col cols="2">
                <v-text-field
                  v-model.number="formData.quantity"
                  :disabled="isEditing"
                  :label="`${$t('generalAttributes.quantity')}*`"
                  :rules="[requiredQuantityRule]"
                  :error-messages="quantityErrorMessages"
                  type="number"
                  min="1"
                  step="1" />
              </v-col>
              <v-col v-if="isEditing" cols="12">
                <MetadataBox
                  :transaction="transaction"
                  :users="users"
                  :waste-codes="waste_codes" />
              </v-col>
              <v-col v-else cols="12">
                <VariantDetails :variant="selectedVariant" />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-if="effectiveTransactionType === 'ADD'"
                  v-model="formData.supplier_id"
                  :items="supplierOptions"
                  :disabled="isEditing"
                  :label="supplierLabel"
                  :rules="supplierRules"
                  :no-data-text="$t('entities.suppliers.notFound')"
                  item-text="text"
                  item-value="value"
                  clearable />
              </v-col>
              <v-col v-if="allowTransactionTypeSelection" cols="12" md="6">
                <v-select
                  v-model="formData.type_code"
                  :items="transactionTypeOptions"
                  :label="$t('entities.stockTransactions.transactionType')"
                  :rules="[requiredStringRule]"
                  :disabled="isEditing"
                  item-text="text"
                  item-value="value" />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="formData.description"
                  :disabled="isFieldDisabled('description')"
                  :label="$t('miscellaneous.description')"
                  rows="2"
                  auto-grow
                  dense />
              </v-col>
              <v-col cols="12">
                <v-file-input
                  ref="filesInput"
                  v-model="selectedFiles"
                  :disabled="isFilesInputDisabled"
                  :label="$t('miscellaneous.files')"
                  :loading="isUploadingFiles"
                  :error-messages="fileUploadErrorMessages"
                  accept=".pdf,image/*"
                  multiple
                  chips
                  show-size
                  counter
                  @change="handleFilesSelection" />
                <div v-if="formData.files.length" class="d-flex flex-wrap mt-2">
                  <v-chip
                    v-for="(filename, index) in formData.files"
                    :key="`${filename}-${index}`"
                    class="mr-2 mb-2"
                    small
                    :close="isFieldEditable('files') && !isEditing"
                    @click="downloadFile(filename)"
                    @click:close="removeUploadedFile(index, $event)">
                    {{ filename }}
                  </v-chip>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-divider />
      <v-card-actions class="py-4 px-5">
        <v-btn @click="close" text>{{ $t('miscellaneous.cancel') }}</v-btn>
        <v-spacer />
        <v-btn
          @click="submit"
          :disabled="isLoading"
          :loading="isLoading"
          color="primary">
          {{ submitLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import user from '@/mixins/crud/computed/user'
import FilesApiV2 from '@/services/misc/FilesApiV2'
import MetadataBox from './MetadataBox.vue'
import VariantDetails from './VariantDetails.vue'

const NOT_ENOUGH_WASTE_TRANSLATION_KEY =
  'entities.stockTransactions.errors.notEnoughWaste'
const NOT_ENOUGH_AVAILABLE_TRANSLATION_KEY =
  'entities.stockTransactions.errors.notEnoughAvailable'
const WASTE_TRANSACTION_CODES = ['WASTE', 'RESOLVE_WASTE']
const TRANSACTION_TYPE_CODES = ['ADD', 'REMOVE', ...WASTE_TRANSACTION_CODES]

const defaultPayload = (data = {}, currentUserId = null, typeCode = 'ADD') => ({
  id: data.id || '',
  arena_variant_id: data.arena_variant_id || '',
  user_id:
    typeof data.user_id === 'number' && !Number.isNaN(data.user_id)
      ? data.user_id
      : currentUserId,
  type_code: data.type_code || typeCode || 'ADD',
  supplier_id: data.supplier_id || '',
  description: data.description || '',
  files: Array.isArray(data.files) ? [...data.files] : [],
  quantity:
    Math.abs(
      WASTE_TRANSACTION_CODES.includes(typeCode)
        ? data.waste_stock_after - data.waste_stock_before
        : data.available_stock_after - data.available_stock_before,
    ) || 1,
})

export default {
  name: 'StockTransactionUpsertDialog',
  mixins: [user],
  props: {
    transaction: { type: Object, default: () => ({}) },
    isEditing: { type: Boolean, required: false },
    isOpened: { type: Boolean, required: true },
    isLoading: { type: Boolean, default: false },
    arenaVariants: { type: Array, default: () => [] },
    suppliers: { type: Array, default: () => [] },
    transactionType: { type: String, required: true },
    arenaId: { type: [String, Number], default: null },
    serverError: { type: Object, default: null },
    users: { type: Array, default: () => [] },
    allowTransactionTypeSelection: { type: Boolean, default: false },
    initialVariantId: { type: String, default: null },
  },
  components: {
    MetadataBox,
    VariantDetails,
  },
  data() {
    return {
      formData: defaultPayload(
        this.transaction,
        this.currentUserId,
        this.transactionType,
      ),
      quantityServerErrorMessage: '',
      selectedFiles: [],
      isUploadingFiles: false,
      fileUploadErrorMessage: '',
      isResettingFilesInput: false,
    }
  },
  computed: {
    currentUserId() {
      const userId = this.user?.id
      return typeof userId === 'number' && !Number.isNaN(userId) ? userId : null
    },
    effectiveTransactionType() {
      if (
        this.allowTransactionTypeSelection &&
        this.formData?.type_code &&
        typeof this.formData.type_code === 'string'
      ) {
        return this.formData.type_code
      }
      return this.transactionType
    },
    transactionTypeOptions() {
      return TRANSACTION_TYPE_CODES.map((code) => ({
        text: this.$t(`entities.stockTransactions.types.${code}`),
        value: code,
      }))
    },
    transactionTypeLabel() {
      const labelMap = {
        ADD: 'addStock',
        REMOVE: 'removeStock',
        WASTE: 'wasteStock',
        RESOLVE_WASTE: 'resolveWaste',
      }
      const localeKey = labelMap[this.effectiveTransactionType]
      return this.$t(`entities.stockTransactions.${localeKey}`)
    },
    dialogTitle() {
      return this.isEditing
        ? `${this.$t('miscellaneous.edit')}: ${this.$t(
            `entities.stockTransactions.types.${this.transactionType}`,
          )}`
        : this.$t(
            `entities.stockTransactions.types.${this.effectiveTransactionType}`,
          )
    },
    submitLabel() {
      return this.isEditing
        ? `${this.$t('miscellaneous.save')}`
        : this.transactionTypeLabel
    },
    requiredStringRule() {
      return (value) =>
        (!!value && value !== '') || this.$t('validation.required')
    },
    requiredNumberRule() {
      return (value) =>
        (value !== null && value !== undefined && value !== '') ||
        this.$t('validation.required')
    },
    requiredQuantityRule() {
      return (value) => {
        const numericValue = Number(value)
        if (numericValue === null || numericValue === undefined) {
          return this.$t('validation.required')
        }
        if (Number.isNaN(numericValue)) {
          return this.$t('validation.numeric')
        }
        if (numericValue < 1) {
          return this.$t('validation.positive')
        }
        if (numericValue > 10000) {
          return this.$t('validation.max', { amount: 10000 })
        }
        return true
      }
    },
    variantOptions() {
      if (!Array.isArray(this.arenaVariants)) return []
      return this.arenaVariants.map((variant) => ({
        text: variant?.name || variant?.sku || variant?.id,
        value: variant?.id,
      }))
    },
    selectedVariant() {
      if (!Array.isArray(this.arenaVariants)) return null
      const variantId = this.formData?.arena_variant_id
      const matchedVariant = this.arenaVariants.find(
        (variant) => variant?.id === variantId,
      )
      return matchedVariant || null
    },
    selectedVariantImage() {
      return this.selectedVariant?.image || ''
    },
    supplierOptions() {
      if (!Array.isArray(this.suppliers)) return []
      return this.suppliers.map((supplier) => ({
        text: supplier?.name || supplier?.id,
        value: supplier?.id,
      }))
    },
    supplierRules() {
      if (!this.isSupplierRequired || !this.isFieldEditable('supplier_id')) {
        return []
      }
      return [this.requiredStringRule]
    },
    isSupplierRequired() {
      return this.formData?.type_code === 'ADD'
    },
    supplierLabel() {
      if (!this.isSupplierRequired) return this.$t('entities.suppliers.entity')
      return `${this.$t('entities.suppliers.entity')}*`
    },
    quantityErrorMessages() {
      return this.quantityServerErrorMessage
        ? [this.quantityServerErrorMessage]
        : []
    },
    isFilesInputDisabled() {
      return this.isFieldDisabled('files') || this.isUploadingFiles
    },
    fileUploadErrorMessages() {
      return this.fileUploadErrorMessage ? [this.fileUploadErrorMessage] : []
    },
    waste_codes() {
      return WASTE_TRANSACTION_CODES
    },
  },
  methods: {
    buildPayload() {
      const payload = this.formData.id
        ? this.buildUpdatePayload()
        : this.buildCreatePayload()
      return payload
    },
    buildUpdatePayload() {
      const { description, files, id } = this.formData
      const payload = { id }
      if (description) payload.description = description
      if (files.length) payload.files = files
      return payload
    },
    buildCreatePayload() {
      const { supplier_id, description, files, ...payload } = this.formData
      if (supplier_id) payload.supplier_id = supplier_id
      if (description) payload.description = description
      if (files.length) payload.files = files
      return payload
    },
    async handleFilesSelection(files) {
      if (this.isResettingFilesInput) return
      if (!files) {
        this.resetFilesInput()
        return
      }
      let selectedFiles = []
      if (Array.isArray(files)) {
        selectedFiles = files.filter(Boolean)
      } else if (typeof FileList !== 'undefined' && files instanceof FileList) {
        selectedFiles = Array.from(files).filter(Boolean)
      } else if (files) {
        selectedFiles = [files]
      }
      if (!selectedFiles.length) {
        this.resetFilesInput()
        return
      }
      await this.uploadSelectedFiles(selectedFiles)
    },
    async uploadSelectedFiles(files) {
      if (!this.arenaId) {
        this.fileUploadErrorMessage = 'Select an arena before uploading files.'
        this.resetFilesInput()
        return
      }
      this.isUploadingFiles = true
      this.clearFileUploadError()
      try {
        const uploads = files.map((file) =>
          FilesApiV2.upload(String(this.arenaId), file)
            .then((response) => ({ success: true, response }))
            .catch((error) => ({ success: false, error })),
        )
        const responses = await Promise.all(uploads)
        const uploadedFileNames = []
        let hasFailure = false
        responses.forEach((result) => {
          if (result.success) {
            const fileName = result.response?.data?.file_name
            if (typeof fileName === 'string' && fileName.length) {
              uploadedFileNames.push(fileName)
            } else {
              hasFailure = true
            }
          } else {
            hasFailure = true
            console.error('File upload failed', result.error)
          }
        })
        if (uploadedFileNames.length) {
          this.formData.files = [...this.formData.files, ...uploadedFileNames]
        }
        if (hasFailure) {
          this.notifyFileUploadError()
        }
      } catch (error) {
        console.error('Failed to upload files', error)
        this.notifyFileUploadError()
      } finally {
        this.resetFilesInput()
        this.isUploadingFiles = false
      }
    },
    resetFilesInput() {
      this.isResettingFilesInput = true
      this.selectedFiles = []
      if (
        this.$refs.filesInput &&
        typeof this.$refs.filesInput.reset === 'function'
      ) {
        this.$refs.filesInput.reset()
      }
      this.$nextTick(() => {
        this.isResettingFilesInput = false
      })
    },
    removeUploadedFile(index, event) {
      if (event && typeof event.stopPropagation === 'function') {
        event.stopPropagation()
      }
      if (
        !this.isFieldEditable('files') ||
        !Array.isArray(this.formData.files) ||
        index < 0
      ) {
        return
      }
      this.formData.files = this.formData.files.filter((_, i) => i !== index)
    },
    notifyFileUploadError() {
      this.fileUploadErrorMessage = this.$t('miscellaneous.fileUploadFailed')
    },
    clearFileUploadError() {
      this.fileUploadErrorMessage = ''
    },
    async downloadFile(filename) {
      const arenaId = this.arenaId
      if (!arenaId) {
        console.warn('Missing arena id for file download')
        return
      }
      try {
        const response = await FilesApiV2.download(String(arenaId), filename)
        const url = response.data?.url
        if (url) {
          this.triggerDownload(url, filename)
        } else {
          throw new Error('missing_download_url')
        }
      } catch (error) {
        console.error('Failed to download file', error)
        this.notifyFileDownloadError()
      }
    },
    notifyFileDownloadError() {
      this.showSnackbar?.({
        color: 'error',
        message: this.$t && this.$t('miscellaneous.fileDownloadFailed'),
      })
    },
    triggerDownload(url, filename) {
      const link = document.createElement('a')
      link.href = url
      link.target = '_blank'
      if (filename) link.download = filename
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    close() {
      this.$emit('dialog:close')
    },
    onOpen() {
      if (this.$refs.form) this.$refs.form.resetValidation()
      const transactionData = {
        ...this.transaction,
        arena_variant_id:
          this.transaction?.arena_variant_id || this.initialVariantId || '',
      }
      this.formData = defaultPayload(
        transactionData,
        this.currentUserId,
        this.transactionType,
      )
      this.clearQuantityServerError()
      this.resetFilesInput()
      this.clearFileUploadError()
    },
    onClose() {
      if (this.$refs.form) this.$refs.form.resetValidation()
      this.formData = defaultPayload(
        {},
        this.currentUserId,
        this.transactionType,
      )
      this.clearQuantityServerError()
      this.resetFilesInput()
      this.clearFileUploadError()
    },
    isFieldEditable(field) {
      if (!Array.isArray(this.editableFields) || !this.editableFields.length) {
        return true
      }
      return this.editableFields.includes(field)
    },
    isFieldDisabled(field) {
      return this.isLoading || !this.isFieldEditable(field)
    },
    submit() {
      if (this.isUploadingFiles) return
      if (!this.$refs.form || !this.$refs.form.validate()) return
      this.$emit(
        'stock-transaction:upsert',
        this.buildPayload(),
        this.effectiveTransactionType,
      )
    },
    applyServerError(error) {
      if (!error) {
        this.clearQuantityServerError()
        return
      }
      const errorKey = error?.response?.data?.l10n_message_key
      if (errorKey === 'shop.ERR029.message') {
        this.quantityServerErrorMessage = this.$t(
          NOT_ENOUGH_WASTE_TRANSLATION_KEY,
        )
        return
      }
      if (errorKey === 'shop.ERR028.message') {
        const availableStock = this.extractFirstParenthesisValue(
          error?.response?.data?.message,
        )
        const action = this.transactionTypeLabel
        this.quantityServerErrorMessage = this.$t(
          NOT_ENOUGH_AVAILABLE_TRANSLATION_KEY,
          {
            availableStock,
            action,
          },
        )
        return
      }
      this.clearQuantityServerError()
    },
    extractFirstParenthesisValue(message) {
      if (!message || typeof message !== 'string') return null
      const match = message.match(/\(([^)]+)\)/)
      return match ? match[1] : null
    },
    clearQuantityServerError() {
      this.quantityServerErrorMessage = ''
    },
  },
  watch: {
    isOpened(value) {
      return value ? this.onOpen() : this.onClose()
    },
    transactionType(newType) {
      if (!newType) return
      this.formData = {
        ...this.formData,
        type_code: newType,
      }
    },
    serverError(value) {
      this.applyServerError(value)
    },
    'formData.quantity'() {
      if (this.quantityServerErrorMessage) {
        this.clearQuantityServerError()
      }
    },
  },
}
</script>

<style scoped>
.variant-field__thumbnail {
  width: 50px;
  height: 50px;
  border-radius: 2px;
  object-fit: cover;
  flex-shrink: 0;
}

.variant-field__thumbnail--placeholder {
  background-color: #e0e0e0;
}
</style>
